import { type ConnectResult, getConnectResult } from '@/connect';
import { useAuthStore } from '@/store/auth-store';
import { DescMessage, DescService, MessageShape } from '@bufbuild/protobuf';
import {
  Client,
  type Interceptor,
  StreamRequest,
  UnaryRequest,
  type UnaryResponse,
  createClient,
} from '@connectrpc/connect';
import { createConnectTransport } from '@connectrpc/connect-web';

type ReplaceReturnType<T extends (...a: never[]) => unknown, TNewReturn> = (
  ...a: Parameters<T>
) => TNewReturn;

type InterceptedPromiseClient<Service extends DescService> = {
  [M in keyof Client<Service>]: ReplaceReturnType<
    Client<Service>[M],
    Promise<ConnectResult<Awaited<ReturnType<Client<Service>[M]>>>>
  >;
};

let accessToken: null | string = null;
export function setConnectAccessToken(token: null | string): void {
  accessToken = token;
}

function setAuthorizationToken(
  req: UnaryRequest<DescMessage, DescMessage> | StreamRequest<DescMessage, DescMessage>,
  token: null | string
) {
  if (token !== null) req.header.set('Authorization', `Bearer ${token}`);
}

const addAuthorizationHeader: Interceptor = (next) => async (req) => {
  setAuthorizationToken(req, accessToken);
  return next(req);
};

const transformData: Interceptor = (next) => async (req) => {
  const authStore = useAuthStore();

  // will be updated
  let result = undefined as unknown as UnaryResponse;

  function getTransformedData() {
    return getConnectResult(async () => {
      result = (await next(req)) as UnaryResponse;
      return result.message;
    });
  }

  let transformedData = await getTransformedData();
  if (
    !transformedData.success &&
    ['rest.MissingAuthToken', 'rest.InvalidAuthToken'].includes(transformedData.error)
  ) {
    // get new token
    accessToken = await authStore.refreshAuthTokens();
    setAuthorizationToken(req, accessToken);
    // use new token inside
    transformedData = await getTransformedData();
  }

  return {
    ...result,
    message: transformedData as unknown as MessageShape<DescMessage>,
  };
};

const transport = createConnectTransport({
  baseUrl: '',
  interceptors: [addAuthorizationHeader, transformData],
  // (default) use JSON format instead of binary
  // easier for debugging and gzip makes it close to the binary format in size
  useBinaryFormat: false,
});

export function createPromiseClient<T extends DescService>(
  service: T
): InterceptedPromiseClient<T> {
  return createClient(service, transport) as unknown as InterceptedPromiseClient<T>;
}
